import { useEffect, useRef } from 'react';

const FOCUSABLE_ELEMENTS = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const useFocusTrap = (
    ref: React.RefObject<HTMLElement | null>,
    isActive: boolean = true,
    initialFocusRef?: React.RefObject<HTMLElement | null>,
    onEscape?: () => void
) => {
    // Die aktuelle Escape-Funktion über eine Ref statt als Abhängigkeit: Aufrufer übergeben oft bei jedem
    // Rendern eine neue Funktion. Der Effekt liefe sonst bei jedem Tastendruck neu und setzte den Fokus
    // nach 50 ms auf das erste Feld zurück, mitten beim Tippen.
    const onEscapeRef = useRef(onEscape);
    useEffect(() => {
        onEscapeRef.current = onEscape;
    });

    useEffect(() => {
        if (!isActive) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!ref.current) return;

            if (e.key === 'Escape' && onEscapeRef.current) {
                onEscapeRef.current();
                return;
            }

            if (e.key !== 'Tab') return;

            const focusableElements = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS);
            const elementsArray = Array.from(focusableElements).filter(el => {
                // Filter out elements that are not visible
                return el.offsetWidth > 0 && el.offsetHeight > 0 && window.getComputedStyle(el).visibility !== 'hidden';
            });

            if (elementsArray.length === 0) {
                e.preventDefault();
                return;
            }

            const firstElement = elementsArray[0];
            const lastElement = elementsArray[elementsArray.length - 1];

            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement || document.activeElement === ref.current) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        // Remember previous focus to restore it when modal closes
        const previousFocus = document.activeElement as HTMLElement;

        // Initial focus
        const focusInitial = () => {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
            } else if (ref.current) {
                const focusableElements = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS);
                const elementsArray = Array.from(focusableElements).filter(el => {
                    return el.offsetWidth > 0 && el.offsetHeight > 0 && window.getComputedStyle(el).visibility !== 'hidden';
                });
                if (elementsArray.length > 0) {
                    elementsArray[0].focus();
                } else {
                    ref.current.focus(); // Fallback to container
                }
            }
        };

        // Delay initial focus slightly to ensure render is complete (e.g. framer-motion animations)
        const timeoutId = setTimeout(focusInitial, 50);

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('keydown', handleKeyDown);
            // Restore previous focus
            if (previousFocus && typeof previousFocus.focus === 'function') {
                // Short timeout to avoid scrolling jump issues
                setTimeout(() => previousFocus.focus(), 10);
            }
        };
    }, [isActive, ref, initialFocusRef]);
};
